import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getFAQs } from '../sanity/client';
import { useCachedTexts } from '../hooks/useCachedTexts';
import './FAQSection.css';

const FAQItem = ({ faq, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, index * 100);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={itemRef}
      className={`faq-item ${isVisible ? 'visible' : ''}`}
    >
      <div className="faq-question-wrapper">
        <span className="faq-number">0{index + 1}</span>
        <h3 className="faq-question">{faq.question}</h3>
      </div>
      <p className="faq-answer" style={{ whiteSpace: 'pre-line' }}>{faq.answer}</p>
    </div>
  );
};

const FAQSection = () => {
  const { t, i18n } = useTranslation();
  const [headerVisible, setHeaderVisible] = useState(false);
  const headerRef = useRef(null);
  const [faqsData, setFaqsData] = useState([]);
  const { getText } = useCachedTexts('about_faq');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const faqs = await getFAQs();
        setFaqsData(faqs);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);


  const faqs = faqsData.map(faq => ({
    question: i18n.language === 'it' ? faq.questionIt : faq.questionEn,
    answer: i18n.language === 'it' ? faq.answerIt : faq.answerEn
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="faq-section">
      <div className="faq-container">
        <div ref={headerRef} className={`faq-section-header ${headerVisible ? 'visible' : ''}`}>
          <h2>{getText('title', t('faq.title'))}</h2>
          <p>{getText('subtitle', t('faq.subtitle'))}</p>
        </div>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
